import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  TrendingUp, TrendingDown, Minus, BarChart3, CalendarRange, Sparkles, Copy, Check,
} from 'lucide-react';
import { usePost } from '@/lib/useData';
import api from '@/lib/api';
import { SectionCard } from '@/components/common/SectionCard';
import { CardSkeleton } from '@/components/common/Loading';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorState } from '@/components/common/ErrorState';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { LabeledBars } from '@/components/charts/LabeledBars';
import { fmtInt, fmtPct, fmtEur, fmtNum, fmtDate } from '@/lib/format';

const MONTHS_FR = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

const eur0 = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0);

// Human, French, NEVER-truncated period labels for every granularity.
const periodLabel = (period, gran) => {
  const p = String(period);
  if (gran === 'day') {
    const [, m, d] = p.split('-');
    return `${Number(d)} ${MONTHS_FR[Number(m) - 1] || m}`;
  }
  if (gran === 'week') {
    const [y, w] = p.split('-S');
    return `${w ? `S${w}` : p} ${String(y).slice(2)}`;
  }
  if (gran === 'month') {
    const [y, m] = p.split('-');
    return `${MONTHS_FR[Number(m) - 1] || m} ${String(y).slice(2)}`;
  }
  return p; // year
};

const GRANS = [
  { key: 'day', label: 'Jour' },
  { key: 'week', label: 'Semaine' },
  { key: 'month', label: 'Mois' },
  { key: 'year', label: 'Année' },
];

// Max number of bars for which the value is printed on top of each bar.
const LABEL_MAX_BARS = 45;

// KPI presentation: full formatter (tooltip/cards), compact bar label, chart
// colour (design tokens only), and trend polarity.
const KPI_META = {
  bs_reel: { fmt: (v) => fmtInt(v), label: (v) => fmtInt(v), color: 'var(--tenant-accent-500)', better: 'up' },
  bs_rue: { fmt: (v) => fmtInt(v), label: (v) => fmtInt(v), color: 'hsl(var(--chart-3))', better: 'up' },
  taux_transfo: { fmt: (v) => fmtPct(v), label: (v) => `${fmtNum(v * 100, 0)}%`, color: 'hsl(var(--chart-4))', better: 'up', pct: true },
  don_moyen: { fmt: (v) => fmtEur(v), label: (v) => `${fmtNum(v, 1)}€`, color: 'hsl(var(--chart-2))', better: 'up' },
  bs_par_jour: { fmt: (v) => fmtNum(v, 2), label: (v) => fmtNum(v, 1), color: 'hsl(var(--chart-1))', better: 'up' },
  montant: { fmt: (v) => eur0(v), label: (v) => eur0(v), color: 'hsl(var(--chart-4))', better: 'up' },
  age_moyen: { fmt: (v) => `${fmtNum(v, 1)} ans`, label: (v) => fmtNum(v, 1), color: 'hsl(var(--chart-2))', better: 'neutral' },
  jours_actifs: { fmt: (v) => fmtInt(v), label: (v) => fmtInt(v), color: 'hsl(var(--chart-3))', better: 'neutral' },
};

// Trend chip: arrow + delta vs the previous period (colour driven by polarity).
const TrendChip = ({ kpi, trend }) => {
  if (!trend || trend.previous === null || trend.previous === undefined) {
    return <span className="text-[10px] text-muted-foreground">1re période</span>;
  }
  const meta = KPI_META[kpi.key] || {};
  const delta = trend.delta || 0;
  const neutral = kpi.better === 'neutral';
  const flat = Math.abs(delta) < 1e-9;
  const good = delta > 0;
  const Icon = flat ? Minus : good ? TrendingUp : TrendingDown;
  const cls = neutral || flat ? 'text-muted-foreground' : good ? 'text-emerald-600' : 'text-rose-500';
  let shown;
  if (meta.pct) {
    shown = `${delta >= 0 ? '+' : ''}${fmtNum(delta * 100, 1)} pts`;
  } else if (trend.pct_change !== null && trend.pct_change !== undefined) {
    shown = `${trend.pct_change >= 0 ? '+' : ''}${fmtNum(trend.pct_change * 100, 0)}\u202f%`;
  } else {
    shown = `${delta >= 0 ? '+' : ''}${meta.fmt ? meta.fmt(delta) : delta}`;
  }
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-medium ${cls}`}>
      <Icon className="h-3 w-3" />{shown}
    </span>
  );
};

// Clickable KPI card — doubles as the metric selector for the histogram.
const KpiCard = ({ kpi, value, trend, selected, onClick }) => {
  const meta = KPI_META[kpi.key] || {};
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="evolution-kpi-card"
      aria-pressed={selected}
      className={`flex flex-col items-start rounded-xl border bg-card p-3 text-left transition-[transform,border-color,box-shadow] duration-200 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--tenant-accent-400)] ${
        selected ? 'border-[color:var(--tenant-accent-500)] ring-2 ring-[color:var(--tenant-accent-400)]/40 card-shadow' : 'hover:border-[color:var(--tenant-accent-300)]'
      }`}
    >
      <span className="flex items-center gap-1.5">
        <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: meta.color }} />
        <span className="text-[11px] text-muted-foreground break-words">{kpi.label}</span>
      </span>
      <span className="mt-1 font-display text-lg font-semibold tabular text-foreground break-words">
        {meta.fmt ? meta.fmt(value) : value}
      </span>
      <span className="mt-0.5"><TrendChip kpi={kpi} trend={trend} /></span>
    </button>
  );
};

// Lightweight Markdown renderer for the AI brief (bold **headings**, "-" bullets).
const inline = (t) => String(t).split(/(\*\*[^*]+\*\*)/g).map((seg, i) => {
  const m = seg.match(/^\*\*([^*]+)\*\*$/);
  return m ? <strong key={i} className="font-semibold text-foreground">{m[1]}</strong> : <span key={i}>{seg}</span>;
});

const BriefContent = ({ text }) => {
  const blocks = [];
  let bullets = [];
  const flush = () => { if (bullets.length) { blocks.push({ type: 'ul', items: bullets }); bullets = []; } };
  String(text || '').split('\n').forEach((raw) => {
    const line = raw.trim();
    if (!line) { flush(); return; }
    if (/^\*\*[^*]+\*\*$/.test(line)) { flush(); blocks.push({ type: 'h', text: line.replace(/\*\*/g, '') }); return; }
    if (/^[-•]\s+/.test(line)) { bullets.push(line.replace(/^[-•]\s+/, '')); return; }
    flush();
    blocks.push({ type: 'p', text: line });
  });
  flush();
  return (
    <div className="space-y-2" data-testid="recruiter-ai-brief-text">
      {blocks.map((b, i) => {
        if (b.type === 'h') {
          return <p key={i} className="mt-3 font-display text-sm font-semibold accent-text first:mt-0 break-words">{b.text}</p>;
        }
        if (b.type === 'ul') {
          return (
            <ul key={i} className="list-disc space-y-1 pl-5">
              {b.items.map((it, j) => <li key={j} className="text-sm leading-6 text-foreground break-words">{inline(it)}</li>)}
            </ul>
          );
        }
        return <p key={i} className="text-sm leading-6 text-foreground break-words">{inline(b.text)}</p>;
      })}
    </div>
  );
};

export const RecruiterEvolution = ({ recruiter, body }) => {
  const [gran, setGran] = useState('month');
  const [metric, setMetric] = useState('bs_reel');

  // AI brief state
  const [briefOpen, setBriefOpen] = useState(false);
  const [brief, setBrief] = useState('');
  const [briefLoading, setBriefLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const reqBody = useMemo(
    () => (recruiter ? { ...body, recruiter } : null),
    [recruiter, body]
  );
  const { data, isLoading, isError, refetch } = usePost('/admin/recruiters/evolution', reqBody, !!recruiter);

  const loadBrief = async () => {
    setBriefLoading(true);
    try {
      const { data: d } = await api.post('/admin/recruiters/ai-brief', { ...body, recruiter });
      setBrief(d.narrative || '');
    } catch (e) {
      toast.error('Génération du résumé IA impossible');
    } finally {
      setBriefLoading(false);
    }
  };

  const openBrief = () => {
    setBriefOpen(true);
    if (!brief && !briefLoading) loadBrief();
  };

  const copyBrief = async () => {
    try {
      await navigator.clipboard.writeText(brief);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard unavailable */ }
  };

  if (isError) {
    return <ErrorState body="Impossible de charger l'évolution de ce recruteur." onRetry={refetch} />;
  }

  if (isLoading || !data) {
    return <div className="grid grid-cols-1 gap-4"><CardSkeleton /></div>;
  }

  if (!data.has_data) {
    return (
      <EmptyState
        title="Aucune donnée d'évolution"
        body="Ce recruteur n'a aucun bulletin exploitable dans le périmètre sélectionné." />
    );
  }

  const kpis = data.kpis || [];
  const series = (data.series && data.series[gran]) || [];
  const trends = (data.trends && data.trends[gran]) || {};
  const selMeta = KPI_META[metric] || {};
  const selKpi = kpis.find((k) => k.key === metric) || kpis[0];

  const formatTip = (v) => (selMeta.fmt ? selMeta.fmt(v) : v);
  const formatLabel = (v) => (selMeta.label ? selMeta.label(v) : v);
  const formatY = (v) => {
    if (selMeta.pct) return `${fmtNum(v * 100, 0)}%`;
    if (metric === 'montant' || metric === 'don_moyen') return fmtInt(v);
    return fmtInt(v);
  };
  const showLabels = series.length > 0 && series.length <= LABEL_MAX_BARS;

  const rowsDesc = [...series].reverse();

  return (
    <div className="space-y-4" data-testid="recruiter-evolution">
      {/* Control bar: history note + AI brief + granularity */}
      <div className="flex flex-col gap-3 rounded-2xl border bg-muted/30 p-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-start gap-2 text-xs text-muted-foreground">
          <CalendarRange className="mt-0.5 h-4 w-4 shrink-0 accent-text" />
          <span className="break-words">
            Historique complet du <strong className="text-foreground">{fmtDate(data.first_date)}</strong> au{' '}
            <strong className="text-foreground">{fmtDate(data.last_date)}</strong> · {fmtInt(data.record_count)} bulletins.
            Tendance <strong className="text-foreground">vs période précédente</strong>.
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={openBrief}
            className="gap-1.5 rounded-xl border-[color:var(--tenant-accent-300)]"
            data-testid="recruiter-ai-brief-button"
          >
            <Sparkles className="h-4 w-4 accent-text" /> Résumé IA
          </Button>
          <ToggleGroup
            type="single"
            value={gran}
            onValueChange={(v) => v && setGran(v)}
            variant="outline"
            size="sm"
            className="shrink-0 rounded-xl bg-card p-1"
            data-testid="evolution-granularity"
          >
            {GRANS.map((g) => (
              <ToggleGroupItem
                key={g.key}
                value={g.key}
                data-testid={`evolution-gran-${g.key}`}
                className="rounded-lg px-3 text-xs data-[state=on]:accent-bg-100 data-[state=on]:accent-text"
              >
                {g.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      </div>

      {/* KPI cards (also the histogram metric selector) */}
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4" data-testid="evolution-kpi-grid">
        {kpis.map((k) => {
          const last = series.length ? series[series.length - 1] : null;
          const val = last ? last[k.key] : 0;
          return (
            <KpiCard
              key={k.key}
              kpi={k}
              value={val}
              trend={trends[k.key]}
              selected={metric === k.key}
              onClick={() => setMetric(k.key)}
            />
          );
        })}
      </div>

      {/* Main histogram for the selected KPI */}
      <SectionCard
        title={selKpi?.label || 'Évolution'}
        subtitle={`Histogramme par ${GRANS.find((g) => g.key === gran)?.label.toLowerCase()} · cliquez une carte pour changer d'indicateur${showLabels ? '' : ' · survolez une barre pour la valeur'}`}
        testid="evolution-chart"
        action={<BarChart3 className="h-4 w-4 text-muted-foreground" />}
      >
        {series.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">Pas de données pour cette granularité.</p>
        ) : (
          <LabeledBars
            data={series}
            dataKey={metric}
            name={selKpi?.label || metric}
            color={selMeta.color}
            height={340}
            xKey="period"
            xTickFormatter={(v) => periodLabel(v, gran)}
            formatY={formatY}
            formatTip={formatTip}
            formatLabel={formatLabel}
            showLabels={showLabels}
          />
        )}
      </SectionCard>

      {/* Detailed period table — no truncation, most recent first */}
      <SectionCard
        title="Détail par période"
        subtitle="Tous les indicateurs, période par période (les plus récentes en premier)"
        testid="evolution-table"
      >
        <div className="max-h-80 overflow-y-auto rounded-xl border">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Période</TableHead>
                <TableHead className="text-right">BS validés</TableHead>
                <TableHead className="text-right">BS signés</TableHead>
                <TableHead className="text-right">Transfo.</TableHead>
                <TableHead className="text-right">Don moyen</TableHead>
                <TableHead className="text-right">Montant</TableHead>
                <TableHead className="text-right">Âge moyen</TableHead>
                <TableHead className="text-right">Jours</TableHead>
                <TableHead className="text-right">BS/jour</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rowsDesc.map((p) => (
                <TableRow key={p.period} data-testid="evolution-row">
                  <TableCell className="break-words text-sm font-medium">{periodLabel(p.period, gran)}</TableCell>
                  <TableCell className="text-right tabular font-semibold">{fmtInt(p.bs_reel)}</TableCell>
                  <TableCell className="text-right tabular text-muted-foreground">{fmtInt(p.bs_rue)}</TableCell>
                  <TableCell className="text-right tabular">{fmtPct(p.taux_transfo)}</TableCell>
                  <TableCell className="text-right tabular">{fmtEur(p.don_moyen)}</TableCell>
                  <TableCell className="text-right tabular text-muted-foreground">{eur0(p.montant)}</TableCell>
                  <TableCell className="text-right tabular text-muted-foreground">{fmtNum(p.age_moyen, 1)}</TableCell>
                  <TableCell className="text-right tabular text-muted-foreground">{fmtInt(p.jours_actifs)}</TableCell>
                  <TableCell className="text-right tabular">{fmtNum(p.bs_par_jour, 2)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </SectionCard>

      {/* AI management brief dialog */}
      <Dialog open={briefOpen} onOpenChange={setBriefOpen}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="recruiter-ai-brief-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-lg">
              <Sparkles className="h-5 w-5 accent-text" /> Résumé IA — brief management
            </DialogTitle>
          </DialogHeader>
          <p className="-mt-1 text-xs text-muted-foreground break-words">
            Synthèse générée par IA à partir des indicateurs de <strong className="text-foreground">{recruiter}</strong> — à utiliser en entretien individuel.
          </p>

          {briefLoading ? (
            <div className="space-y-2 py-2" data-testid="recruiter-ai-brief-loading">
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-4/5" />
              <Skeleton className="mt-3 h-4 w-1/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ) : brief ? (
            <div className="rounded-xl border bg-card p-4">
              <BriefContent text={brief} />
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground">Aucun résumé généré.</p>
          )}

          <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={loadBrief}
              disabled={briefLoading}
              className="gap-1.5 rounded-xl"
              data-testid="recruiter-ai-brief-regenerate"
            >
              <Sparkles className="h-4 w-4" /> Régénérer
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={copyBrief}
              disabled={briefLoading || !brief}
              className="gap-1.5 rounded-xl"
              data-testid="recruiter-ai-brief-copy"
            >
              {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
              {copied ? 'Copié' : 'Copier'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
